import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../../../core/store/auth.store';
import { useRouteStore } from '../../../../core/store/route.store';
import { ProfileService } from '../../../private/modules/profile/service/profile.service';
import { User } from '../../../private/modules/profile/model/user.model';
import { route } from '../../../../constants/routes';

const AuthHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuthStore();
  const { setRoutePrefix } = useRouteStore();

  const [profile, setProfile] = useState<User | null>(null);
  const [profileChecked, setProfileChecked] = useState(false);

  const checkInProgressRef = useRef(false);

  const checkAuthAndProfile = useCallback(async () => {
    if (checkInProgressRef.current) return;
    checkInProgressRef.current = true;

    try {
      const token = localStorage.getItem('token');
      const isPasswordSetupPage = location.pathname.includes(
        '/user-registrations'
      );
      const isAccountActivationPage = location.pathname.includes(
        route('/account-activation')
      );
      const isLoginPage = location.pathname === '/login';
      const isRecoveryPasswordPage = location.pathname === '/recovery-password';
      const isLegalPage =
        location.pathname === '/privacy' || location.pathname === '/terms';
      const isPublicCandidatePage =
        location.pathname.startsWith('/clarifications/respond/') ||
        location.pathname.startsWith('/candidate-conversations/') ||
        location.pathname.startsWith('/interviews/confirm/');

      const justFinishedWizard =
        localStorage.getItem('hasJustFinishedWizard') === 'true';
      if (justFinishedWizard) {
        localStorage.removeItem('hasJustFinishedWizard');
        navigate(route('/dashboard'));
      }

      if (
        !token &&
        !isPasswordSetupPage &&
        !isAccountActivationPage &&
        !isLoginPage &&
        !isRecoveryPasswordPage &&
        !isLegalPage &&
        !isPublicCandidatePage
      ) {
        navigate('/login');
        return;
      }

      if (!token || !location.pathname.includes(route('/'))) {
        return;
      }

      if (profileChecked && profile) return;

      const cachedProfile = localStorage.getItem('userProfile');
      let fetchedProfile: User;

      if (
        cachedProfile &&
        cachedProfile !== 'undefined' &&
        cachedProfile !== 'null'
      ) {
        try {
          fetchedProfile = JSON.parse(cachedProfile);
          setProfile(fetchedProfile);
          setRoutePrefix(fetchedProfile.companyName);
        } catch (e) {
          localStorage.removeItem('userProfile');
          fetchedProfile = await ProfileService.Get();
          setProfile(fetchedProfile);
          localStorage.setItem('userProfile', JSON.stringify(fetchedProfile));
          setRoutePrefix(fetchedProfile.companyName);
        }
      } else {
        fetchedProfile = await ProfileService.Get();
        setProfile(fetchedProfile);
        localStorage.setItem('userProfile', JSON.stringify(fetchedProfile));
        setRoutePrefix(fetchedProfile.companyName);
      }

      const needsAccountActivation =
        !fetchedProfile.profileSet ||
        fetchedProfile.companySet === false ||
        !fetchedProfile.firstname ||
        !fetchedProfile.lastname ||
        !fetchedProfile.email ||
        !fetchedProfile.phoneNumber;

      if (needsAccountActivation && !isAccountActivationPage) {
        navigate(route('/account-activation'));
      } else if (!needsAccountActivation && isAccountActivationPage) {
        navigate(route('/dashboard'));
      }

      setProfileChecked(true);
    } catch (error) {
      console.error('Error checking profile:', error);
    } finally {
      checkInProgressRef.current = false;
    }
  }, [location.pathname, navigate, profile, profileChecked, setRoutePrefix]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void checkAuthAndProfile();
    }, 100);

    return () => clearTimeout(timer);
  }, [auth, checkAuthAndProfile]);

  return null;
};

export default AuthHandler;
